import { get } from 'lodash';

// constants
import { UNIT_TYPES } from 'constants/unitTypes';

import { inputStates } from 'constants/values';
import { plantingPlanToHankGoalRatio } from 'constants/goalRatio';
import { ADVANCED_SCRIPTING_AVAILABILITY_SUCCESS } from 'constants/advanced';

// util
import { convert, convertYieldWithNominalWeight } from 'util/unitConversion';
import { calcAreaForZoneGeoJson } from 'util/zoneOperations';
import { generateZoneId } from 'util/zoneCreation';
import { formatNumberRoundToNearest } from 'util/formatters/numberFormatter';
import { getEstimatedYield } from './scenarios/util/densityCurves';

const _rogueToScUnitValue = (rogueUnitValue) => ({
  q:rogueUnitValue.q,
  u:rogueUnitValue.u === UNIT_TYPES.kg_ha ? UNIT_TYPES.kilogram_hectare : rogueUnitValue.u // Normalizing Rogue's kg/ha to our unit type.
});

export const hankToScUnitValue = (hankUnitValue) => ({q:hankUnitValue.q, u:hankUnitValue.u.replace(/_/g, '/') });

const _getAverageYield = (yieldLayer, nominalWeights) => {
  const cropId = get(yieldLayer, 'crop.legacyId');
  const cropNominalWeight = get(nominalWeights, `[${cropId}]`, 0);
  if (!cropNominalWeight) { return null; } // Can't convert to valid value for this crop.
  const from = hankToScUnitValue(get(yieldLayer, 'avgYield'));
  if (!from) { return null; } // Unexpected yield layer value.
  return convertYieldWithNominalWeight({from, toUnit:UNIT_TYPES.bu_ac, cropNominalWeight});
};

export const parseAdvancedScriptLayerResults = results => {
  const badCciLayers = get(results, 'data.asyncRecommendation.recommendation.cciFailedLayers');
  const badYieldLayers = get(results, 'data.asyncRecommendation.recommendation.yieldFailedLayers');
  return { badCciLayers, badYieldLayers };
};

export const parseScriptRecommendations = results => {
  return get(results, 'data.asyncRecommendation.recommendation');
};

export const parseScriptRecommendationsJobId = results => {
  return get(results, 'data.asyncRecommendation.status.jobId');
};

export const parseScriptRecommendationsPollInterval = results => {
  return get(results, 'data.asyncRecommendation.status.waitTime');
};

export const parseAdvancedScriptAvailabilityReadiness = results => {
  return get(results, 'data.targetAvailableData.updateStatus.message') === ADVANCED_SCRIPTING_AVAILABILITY_SUCCESS
    || get(results, 'data.targetAvailableData.updateStatus') === null;
};

export const parseAdvancedScriptAvailabilityPollInterval = results => {
  return get(results, 'data.targetAvailableData.updateStatus.waitTime');
};

const _blankAverageYields = (yieldLayers) => yieldLayers.forEach(yieldLayer => { yieldLayer.averageYield = null; });

const _parseAdjustedAverageYieldForSeason = ({adjustedYields, season}) => {
  const { postHarvestYields } = adjustedYields;
  let adjustedAverageYield = null;
  if (postHarvestYields) {
    postHarvestYields.some(postHarvestYield => {
      if (postHarvestYield.seasonTag === season && postHarvestYield.nominalWeight) {
        const cropNominalWeight = postHarvestYield.nominalWeight;
        const from = _rogueToScUnitValue(postHarvestYield.yieldAverage);
        adjustedAverageYield = convertYieldWithNominalWeight({from, toUnit:UNIT_TYPES.bu_ac, cropNominalWeight});
        return true;
      }
      return false;
    });
  }
  return adjustedAverageYield;
};

const _applyAdjustedAverageYields = (yieldLayers, adjustedYields) => {
  yieldLayers.forEach(yieldLayer => {
    const adjustedAverageYield = _parseAdjustedAverageYieldForSeason(
      {adjustedYields, season:yieldLayer.season, cropId:yieldLayer.cropId});
    if (adjustedAverageYield) {
      yieldLayer.averageYield = adjustedAverageYield;
    }
  });
};

export const parseAdvancedScriptAvailability = ({availability, adjustedYields, cropNominalWeights}) => {

  const tad = get(availability, 'data.targetAvailableData');
  if (!tad) { return { isEnoughDataForAdvancedScript:false }; } // No useful parsing is possible.

  // Parsing strategy - copy over expected data only, and don't fail if any expected values are missing. If service
  // gives us data in unexpected format, and it causes a bug, that is easy enough to troubleshoot back to the service
  // call. So I'm foregoing validation code here or in processor.

  const cciLayers = get(tad, 'cciLayers', []).map( (cciLayer) => ({ season:cciLayer.season }) );
  const plantingLayers = get(tad, 'plantingLayers', [])
  .map((plantingLayer) => ({
    date: plantingLayer.date,
    season:plantingLayer.season,
    cropId:get(plantingLayer, 'crop.legacyId'),
    cropUuid:get(plantingLayer, 'crop.id')
  }) );
  const yieldLayers = get(tad, 'yieldLayers', [])
  .map((yieldLayer) => ({
    date: yieldLayer.date,
    averageYield:_getAverageYield(yieldLayer, cropNominalWeights),
    season:yieldLayer.season,
    cropId:get(yieldLayer, 'crop.legacyId'),
    cropUuid:get(yieldLayer, 'crop.id')
  }) );

  if (adjustedYields) {
    // For each yield layer, use an adjusted average yield if it's available.
    _applyAdjustedAverageYields(yieldLayers, adjustedYields);
  } else { // If no access to adjusted yield, don't use Hank's value either because it indicates current user should not have access.
    _blankAverageYields(yieldLayers);
  }

  const isEnoughForAdvancedScript = get(tad, 'recommendations[0].status') === 'available';
  const recommendedNumZones = get(tad, 'recommendations[0].defaultNumberOfZones', 4); // 4 = a default zone count value that will always work.

  return { isEnoughForAdvancedScript, recommendedNumZones, cciLayers, plantingLayers, yieldLayers };
};

export const parseRate = (hankRate) => {
  const rate = convert({from: hankToScUnitValue(hankRate), to:{u:UNIT_TYPES.seed_acre}});
  rate.q = formatNumberRoundToNearest(rate.q, 100);
  return rate;
};

export const parseGoalRatioValuesFromRecommendedZone = ({
  recommendedZone,
  goalRatio,
  cropNominalWeight,
  grainPrice,
  currentAveragePopulation,
  recommendation
}) => {
  // If the user has entered a current average population we will use the highest economic
  // and use the equations of density curve instead,
  //see https://climate.jira.com/browse/ARTFP-795
  if(currentAveragePopulation?.q) {
    const hankRate = recommendedZone.properties.highestEconomic.rate;
    const metricAvgPopulation = convert({
      from: currentAveragePopulation,
      to: { u: UNIT_TYPES.seed_ha }
    });
    const delta = metricAvgPopulation.q / recommendation.highestEconomicField.rate.q;
    const newRate = { q: hankRate.q * delta, u: hankRate.u };
    const metricTargetYield = recommendedZone.properties.highestYield.estimatedYield;
    const hardinParams = recommendation.densityCurveField.hardinParams;
    const metricEstimatedYield = getEstimatedYield(newRate.q,
      metricTargetYield.q, hardinParams.hardinParamsA, hardinParams.hardinParamsB);
    const estimatedYield = convertYieldWithNominalWeight({
      from: { u: UNIT_TYPES.kg_ha, q: metricEstimatedYield },
      toUnit: UNIT_TYPES.bu_ac,
      cropNominalWeight
    });
    
    // grainPrice comes in the unit of the user, 
    // we need to convert it to imperial to match the scenario's values
    const imperialGrainPrice = convert({
      from: grainPrice,
      to: { u: UNIT_TYPES.usd_bu },
      cropNominalWeight
    });

    const estimatedProfit = { q: imperialGrainPrice.q * estimatedYield.q, u: UNIT_TYPES.usd_acre };
    return {
      estimatedProfit,
      estimatedYield,
      rate: parseRate(newRate)
    };
  }
  const hankGoalRatio = plantingPlanToHankGoalRatio[goalRatio];
  const { estimatedYield, rate } = recommendedZone.properties[hankGoalRatio];
  const estimatedYieldBuAc = convertYieldWithNominalWeight(
    {from:hankToScUnitValue(estimatedYield), toUnit:UNIT_TYPES.bu_ac, cropNominalWeight});
  const price = convert({
    from: grainPrice,
    to: {u: UNIT_TYPES.usd_bu },
    cropNominalWeight
  });
  const estimatedRevenue = {q: price.q * estimatedYieldBuAc.q, u:UNIT_TYPES.usd_acre};
  return { // TODO - SC-1910 the target units should be chosen by user's country.
    estimatedProfit: estimatedRevenue,
    estimatedYield: estimatedYieldBuAc,
    rate: parseRate(rate)
  };
};

const _parseFeatureFromRecommendedZone = ({recommendedZone}) => {
  return {
    type: recommendedZone.type,
    geometry: recommendedZone.geometry,
    properties: {}
  };
};

const _buildTemplatePlantingZone = (
  { zoneId, index, recommendedZone, goalRatio, product, cropNominalWeight, grainPrice, densityCurve,
    currentAveragePopulation, recommendation }) => {
  const { estimatedProfit, estimatedYield, rate }
    = parseGoalRatioValuesFromRecommendedZone({recommendedZone, goalRatio, cropNominalWeight, grainPrice,
      currentAveragePopulation, recommendation});
  const feature = _parseFeatureFromRecommendedZone({recommendedZone});
  const area = calcAreaForZoneGeoJson(feature);
  return {
    meta: {
      isDeadZone: get(recommendedZone, 'properties.missingData'),
      highestYield: recommendedZone.properties.highestYield,
      highestEconomic: recommendedZone.properties.highestEconomic,
      applications: {
        fertilizer: [],
        seed: [{
          ['estimated-profit']: estimatedProfit,
          ['estimated-yield']: estimatedYield,
          product: { id:product.id, name:product.name, brand:product.brand },
          rate,
          ['target-yield']: estimatedYield // Target yield same as estimate for a template.
        }]
      },
      area,
      geometry:feature, // TODO - Why is this in meta? I'm recreating structure from what we currently do in parseScript(), but don't understand it.
      index,
      zoneId,
      inputState: inputStates.INSYNC,
      densityCurve
    },
    geoJson:feature
  };
};

const _buildTemplatePlantingZones = ({
  recommendations, goalRatio, product, cropNominalWeight, grainPrice, currentAveragePopulation}) => {
  const zones = { };
  const features = get(recommendations, 'data.asyncRecommendation.recommendation.features');
  features.forEach((feature, index) => {
    const zoneId = generateZoneId(zones);
    zones[zoneId] = _buildTemplatePlantingZone(
      {zoneId, index, recommendedZone:feature, goalRatio, product, cropNominalWeight, grainPrice,
        densityCurve: feature.properties.densityCurve, currentAveragePopulation,
        recommendation: recommendations.data.asyncRecommendation.recommendation});
  });
  return zones;
};

const _buildTemplatePlantingZoneSummary = (zones) => {
  const summaryZones = Object.values(zones).map(zone => ({id:zone.meta.zoneId, area:zone.meta.area}) );
  return { zones:summaryZones };
};

export const parseAdvancedScriptRecommendationsToTemplateParams = ({
  recommendations, goalRatio, product, cropNominalWeight, grainPrice, currentAveragePopulation
}) => {
  const zones = _buildTemplatePlantingZones({ recommendations, goalRatio, product, cropNominalWeight, grainPrice,
    currentAveragePopulation });
  return { zones, zonesSummary:_buildTemplatePlantingZoneSummary(zones) };
};

export const parseHankRecommendations = recommendations => {
  const parsedRecommendations = recommendations;
  const parsed = parsedRecommendations.data.asyncRecommendation.recommendation.features;
  parsed.forEach((feature, index) => {
    const properties = feature.properties;

    for(const property in properties) {
      if (properties.hasOwnProperty(property)) {
        const prop = properties[property];
        for(const single in prop) {
          if(isNaN(prop[single].q)) {
            parsed[index].properties[property][single].q = 0;
          }
        }
      }
    }
  });

  parsedRecommendations.data.asyncRecommendation.recommendation.features = parsed;
  return parsedRecommendations;
};

export const unitTestPrivate = {
  _getAverageYield,
  _rogueToScUnitValue,
  _parseAdjustedAverageYieldForSeason
};
